import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMap, useMapEvents } from "react-leaflet";
import { latLngBounds } from "leaflet";

import { Order } from "../../../../../../../../services";
import { LocationHistory } from "../../../../../../../../redux/reducers/Archives/GPSLog/interface";
import { useExecutorTrackLayers } from "../../../../../../../../hooks/useExecutorTrackLayers";
import { useNotification } from "../../../../../../../../components/NotificationProvider";
import {
	CircleMarkerList,
	MarkerList,
	PolylineList,
} from "../../../../../../../../components/MapGPS/components";

/**
 * The executor track of the order up to the moment it was mounted.
 * It is not updated in real time: remount it to load the fresh track.
 */
const ExecutorTrack: React.FC<ExecutorTrack.Props> = ({
	orderId,
	executorId,
	closedAt,
}) => {
	const { t } = useTranslation();
	const { info } = useNotification();
	const map = useMap();
	const [zoom, setZoom] = useState(() => map.getZoom());
	const [executorRoute, setExecutorRoute] = useState<LocationHistory[]>([]);

	useMapEvents({ zoomend: () => setZoom(map.getZoom()) });

	useEffect(() => {
		let cancelled = false;

		(async () => {
			const data = await Order.getExecutorRoute({ orderId, executorId });
			if (cancelled) return;

			// On an elastic error the gateway answers with `{ error, message }`
			const res: LocationHistory[] = Array.isArray(data) ? data : [];
			const points = res.flatMap(({ segments }) =>
				(segments || []).flatMap((segment) =>
					segment.map(({ point }) => point),
				),
			);

			if (!points.length) {
				info(
					t("mainPage.archives.orders.modal.map.badges.track") ||
						"Track",
					t(
						"pages.mainPage.pages.orders.orderModal.mapController.str205",
					) || "",
				);
				return;
			}

			setExecutorRoute(res);
			map.fitBounds(latLngBounds(points), { padding: [30, 30] });
		})();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId, executorId]);

	const radius = useMemo(() => {
		const k = 16;
		return Math.max((zoom * k) / 18 - 15, 0);
	}, [zoom]);

	const { positions, trackStartEnd, circleMarkerItems } =
		useExecutorTrackLayers(executorRoute, radius, closedAt);

	if (!executorRoute.length) return null;

	return (
		<>
			<MarkerList data={trackStartEnd} />
			<CircleMarkerList data={circleMarkerItems} />
			<PolylineList points={positions} />
		</>
	);
};

declare namespace ExecutorTrack {
	interface Props {
		orderId: number;
		executorId: number;
		closedAt?: string | number | Date | null;
	}
}

export const ExecutorTrackMemo = memo(ExecutorTrack);
export default ExecutorTrack;
